<script lang="ts" setup>
import VkIcon from '~/assets/icons/social/vk.svg?component'
import TelegramIcon from '~/assets/icons/social/telegram-white.svg?component'
import YoutubeIcon from '~/assets/icons/social/youtube.svg?component'
import LogoImage from '~/assets/icons/logo.svg?component'
import Accordion from '~/components/ui/accordion/Accordion.vue'
import type { IAccordionToggleResult } from '~~/shared/interfaces/ui/accordion'
import ProfileIcon from '~/assets/icons/menuHeader/profile.svg?component'
import useAccountStore from '~/shared/store/modules/account'
import useMainStore from '~/shared/store'
import account from '~/shared/store/modules/account'

const accountStore = useAccountStore()
const mainStore = useMainStore()

const footerNavigation = computed(() => [
  {
    title: 'SOTRANS',
    links: [
      {
        name: 'О компании',
        href: '/about',
      },
      {
        name: 'Контакты',
        href: '/contacts/spb',
      },
      {
        name: 'Новости',
        href: '/news',
      },
      {
        name: 'Запчасти SOTRANS',
        href: '/catalog/katalogi-po-brendam/sotrans',
      },
    ],
  },
  {
    title: 'Покупателю',
    links: [
      {
        name: 'Доставка',
        href: '/delivery',
      },
      {
        name: 'Личный кабинет',
        href: '/account',
        icon: ProfileIcon,
        clickFunc:
          accountStore.userRole === 'guest'
            ? () => mainStore.setAuthModalState(true)
            : null,
      },
      {
        name: 'Акции',
        href: '/promotions',
      },
      {
        name: 'Политика конфиденциальности',
        href: '/terms', // Не правильно назвали страницы, поэтому 'Политика конфиденциальности' - /terms
      },
      {
        name: 'Пользовательское соглашение',
        href: '/privacy', // Не правильно назвали страницы, поэтому 'Пользовательское соглашение' - /privacy
      },
    ],
  },
  {
    title: 'Каталог',
    links: [
      {
        name: 'Грузовые запчасти',
        href: '/catalog/gruzovye-zapchasti',
      },
      {
        name: 'Аккумуляторы',
        href: '/catalog/akkumulyatory',
      },
      {
        name: 'Масла и тех. жидкости',
        href: '/catalog/masla-i-tekhnicheskie-zhidkosti',
      },
      {
        name: 'Шины и диски',
        href: '/catalog/shiny-i-diski',
      },
      {
        name: 'Бренды запчастей',
        href: '/catalog/katalogi-po-brendam',
      },
      {
        name: 'Подбор по VIN-номеру',
        href: '/vin',
      },
    ],
  },
])

const footerMediaLinks = [
  {
    href: 'https://vk.com/sotrans',
    icon: VkIcon,
    title: 'vk',
  },
  {
    href: 'https://t.me/sotrans_official',
    icon: TelegramIcon,
    title: 'telegram',
  },
  {
    href: 'https://www.youtube.com/channel/UCF3fbQNCxGN9Azi3SA3TLoQ',
    icon: YoutubeIcon,
    title: 'youtube',
  },
]

const footerAccordionData = ref<
  {
    title: string
    value: string
    isOpen: boolean
  }[]
>([
  {
    title: 'SOTRANS',
    value: 'sotrans',
    isOpen: false,
  },
  {
    title: 'Покупателю',
    value: 'buyer',
    isOpen: false,
  },
  {
    title: 'Каталог',
    value: 'catalog',
    isOpen: false,
  },
])

function setFooterAccordionData({
  accordionPosition,
  isOpen,
}: IAccordionToggleResult) {
  if (isOpen) {
    return footerAccordionData.value.forEach((footerAccordion, i) => {
      if (accordionPosition == i) {
        footerAccordionData.value[accordionPosition].isOpen = isOpen
      } else {
        footerAccordion.isOpen = false
      }
    })
  }
  footerAccordionData.value[accordionPosition].isOpen = isOpen
}
</script>

<template>
  <footer
    class="footer"
    :class="useRoute().name !== 'order' && 'footer__bottom'"
  >
    <div v-if="useRoute().name !== 'order'" class="footer__up">
      <div class="container">
        <div class="footer__contacts">
          <a class="footer__contacts-link" href="tel:+78002000233"
            >8 (800) 200-02-33</a
          >
          <div class="footer__contacts-time">Ежедневно 9:00-18:00</div>
          <a class="footer__contacts-link" href="mailto:support@sotrans.ru"
            >support@sotrans.ru</a
          >
          <div class="footer__contacts-address">
            Россия, 187015, Ленинградская обл., пгт. Красный
            Бор,ул. Промышленная 3
          </div>
          <div class="footer__contacts-time">Ежедневно 9:00-21:00</div>
          <div class="footer__contacts-media">
            <a
              target="_blank"
              v-for="(mediaLink, i) in footerMediaLinks"
              :key="i + '__media-link'"
              :title="mediaLink.title"
              :href="mediaLink.href"
            >
              <component :is="mediaLink.icon" key="mediaLink.title"></component>
            </a>
          </div>
        </div>
        <div class="footer__accordions">
          <Accordion
            v-for="(acctodionItem, i) in footerAccordionData"
            :key="i + '__mobile-footer-section'"
            :accordion-item="acctodionItem"
            :accordion-position="i"
            @set-accordion-section-status="setFooterAccordionData"
          >
            <template v-slot:accordionContent>
              <ul class="footer__accordions-list">
                <li
                  v-for="(link, j) in footerNavigation[i].links"
                  :key="j + '__mobile-footer-section-link'"
                  class="footer__accordions-list-item"
                >
                  <a :href="link.href">
                    {{ link.name }}
                  </a>
                </li>
              </ul>
            </template>
          </Accordion>
        </div>
        <div class="footer__nav">
          <div
            v-for="(section, i) in footerNavigation"
            :key="i + '__footer-section'"
            class="footer__nav-section"
          >
            <div class="footer__nav-title">{{ section.title }}</div>
            <ul class="footer__nav-list">
              <li
                v-for="(link, j) in section.links"
                :key="j + '__footer-section-link'"
                class="footer__nav-list-item"
                @click="link.clickFunc ? link.clickFunc() : null"
              >
                <NuxtLink :to="link.href">
                  {{ link.name }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="footer__rules">
      <div class="container">
        <div class="footer__rules-section">
          <p class="footer__rules-text">ООО «СОТРАНС»</p>
          <p class="footer__rules-text">ИНН 7825053969</p>
          <p class="footer__rules-text">ОГРН 1027804194813</p>
        </div>
        <div class="footer__rules-section">
          <p class="footer__rules-text">
            © SOTRANS PARTS {{ new Date().getFullYear() }}. Все права защищены
          </p>
          <div class="footer__rules-metrika">
            <!-- Yandex.Metrika informer -->
            <a
              href="https://metrika.yandex.ru/stat/?id=89928957&amp;from=informer"
              target="_blank"
              rel="nofollow"
              ><img
                src="https://informer.yandex.ru/informer/89928957/3_0_4C4D52FF_2C2D32FF_1_uniques"
                style="width: 88px; height: 31px; border: 1px solid #545454"
                alt="Яндекс.Метрика"
                title="Яндекс.Метрика: данные за сегодня (просмотры, визиты и уникальные посетители)"
                class="ym-advanced-informer"
                data-cid="89928957"
                data-lang="ru"
            /></a>
            <!-- /Yandex.Metrika informer -->
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.footer {
  &__bottom {
    @include md-breakpoint {
      padding-bottom: 78px;
    }
  }
  &__up {
    background-color: $footer_bg-up;
    padding: 40px 0;
    @include md-breakpoint {
      padding: 28px 0;
    }

    .container {
      display: flex;
      justify-content: space-between;
      gap: 40px;
      @include md-breakpoint {
        gap: 28px;
        flex-direction: column;
      }
    }
  }

  &__contacts {
    color: $control-content_white;
    max-width: 266px;

    &-link {
      color: $control-content_white;
      @include font(18px, 24px, 600);

      &:nth-of-type(2) {
        margin-top: 20px;
      }

      &:hover:not(:active) {
        @include mf-breakpoint {
          color: $control-content_orange;
        }
      }
    }

    &-time {
      color: $control-content_tretiary;
      @include font(14px, 20px);
    }

    &-address {
      @include font(14px, 20px);
      margin: 20px 0 4px;
    }

    &-media {
      display: flex;
      gap: 12px;
      margin-top: 24px;

      a {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 48px;
        height: 48px;
        border-radius: 8px;
        background-color: $footer_control-bg-grey;

        &:hover:not(:active) {
          @include mf-breakpoint {
            background-color: $footer_control-bg-grey-hover;
          }
        }
      }
    }
  }

  &__nav {
    max-width: 830px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    @include md-breakpoint {
      display: none;
    }

    &-section {
      max-width: 200px;
    }

    &-title {
      font-weight: 500;
      font-size: inherit;
      color: $content-white;
      margin-bottom: 16px;
    }

    &-list {
      &-item {
        @include font(14px, 20px);
        &:not(:last-child) {
          margin-bottom: 12px;
        }
      }
    }

    a {
      color: $footer_control-content-tretiary;

      &:hover:not(:active) {
        @include mf-breakpoint {
          color: $control-content_white;
        }
      }
    }
  }

  &__accordions {
    display: none;
    @include md-breakpoint {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .accordion {
      :deep(.accordion-item__header) {
        padding: 12px 16px;
      }
      :deep(.accordion-item__header-title) {
        @include font(18px, 24px, 600);
      }
      :deep(.accordion-item__header-icon) {
        path {
          fill: $content_white;
        }
      }
    }

    &-list {
      padding-top: 20px;
      display: flex;
      flex-direction: column;
      gap: 20px;

      &-item {
        padding: 0 16px;
        a {
          color: $content_icons;
        }
      }
    }
  }

  &__rules {
    background-color: $footer_bg-down;
    color: $content_tretiary;
    @include font(14px, 20px);
    padding: 14px 0;
    @include md-breakpoint {
      padding: 20px 0;
    }

    &-section {
      display: flex;
      gap: 16px;
      align-items: center;
      @include md-breakpoint {
        flex-direction: column;
        align-items: flex-start;
      }

      &:first-child {
        @include md-breakpoint {
          gap: 4px;
        }
      }

      &:last-child {
        @include md-breakpoint {
          gap: 20px;
        }
      }
    }

    .container {
      display: flex;
      justify-content: space-between;
      @include md-breakpoint {
        flex-direction: column;
        gap: 20px;
      }
    }
  }
}
</style>
